<template>
	<!-- Don't show inventory status badge for outlet-only products (since it wouldn't make sense there)
     OR service products -->
	<template v-if="!(isOutletProduct || isServiceProduct)">
		<!-- fi texts -->
		<template v-if="locale === 'fi'">
			<span
				v-if="text"
				:class="[
					...badgeBaseClasses,
					status === ProductInventoryStatus.IN_STOCK
						? badgeBgColors.bright
						: badgeBgColors.muted,
				]"
			>
				{{ text }}
			</span>
		</template>

		<!-- ee texts -->
		<template v-else>
			<div v-if="deliveryEstimateDays > 0">
				<span
					:class="[
						...badgeBaseClasses,
						deliveryEstimateDays <= 7
							? badgeBgColors.bright
							: badgeBgColors.muted,
					]"
				>
					{{ formatDays(deliveryEstimateDays) }}
				</span>
			</div>
		</template>
	</template>
</template>

<script lang="ts" setup>
import type {Product} from "@apptus/esales-api";

import {useDeliveryEstimateText} from "@pkg/shared/composables/use-delivery-estimate-text";
import {useElevateProduct} from "@pkg/shared/composables/use-elevate-product";

const properties = defineProps<{
	product: Product;
}>();

const badgeBgColors = {
	muted: "bg-[#294C2E]",
	bright: "bg-[#118A0A]",
};

// Intermediary type for helping with text and bgColor mapping.
enum ProductInventoryStatus {
	IN_STOCK = "inStock",
	COMING_TO_STOCK = "comingToStock",
	ONLY_IN_STORES = "onlyInStores",
	IS_CUSTOMIZABLE = "isCustomizable",
}

const {t, locale} = useI18n({useScope: "global"});
const {getStringValues} = useElevateProduct(properties.product);
const {formatDays, isCustomizableProductClass} = useDeliveryEstimateText();

const isAvailableForPurchase = computed(() =>
	properties.product.variants.some(
		(x) => x.custom?.isAvailableForPurchase?.[0]?.id === "true",
	),
);

const isAvailableQuickly = computed(() =>
	properties.product.variants.some(
		(x) => x.custom?.isAvailableQuickly?.[0]?.id === "true",
	),
);

const deliveryEstimateDays = computed(() => {
	const days = properties.product.custom?.delivery_estimate_days?.[0]?.id;

	return days ? Number(days) : 0;
});

const type = computed(() => getStringValues("type")[0]);

// So far all outlet products are just concrete variants, makes computing this simpler
const isOutletProduct = computed(
	() =>
		type.value === "SimpleProduct" &&
		getStringValues("inventoryState")[0] === "AVAILABLE_IN_OUTLET",
);

const isServiceProduct = computed(
	() => getStringValues("productClass")[0] === "PALVELU",
);

const status = computed((): ProductInventoryStatus => {
	if (!isAvailableForPurchase.value)
		return ProductInventoryStatus.ONLY_IN_STORES;
	// Customizable (Yksilöi) products
	if (isCustomizableProductClass(getStringValues("productClass")[0]))
		return ProductInventoryStatus.IS_CUSTOMIZABLE;
	if (isAvailableQuickly.value) return ProductInventoryStatus.IN_STOCK;
	// Non-customizable products
	return ProductInventoryStatus.COMING_TO_STOCK;
});

const badgeBaseClasses = [
	"mt-1.5",
	"inline-block",
	"rounded-2xs",
	"p-1",
	"font-sans",
	"text-xs",
	"font-bold",
	"uppercase",
	"text-white",
	"md:p-[5px]",
];

const text = computed(() => {
	switch (status.value) {
		case ProductInventoryStatus.IN_STOCK: {
			return t("delivery.in-stock-abbr");
		}
		case ProductInventoryStatus.COMING_TO_STOCK: {
			return t("delivery.coming-to-stock-abbr");
		}
		case ProductInventoryStatus.ONLY_IN_STORES: {
			return t("delivery.out-of-stock-abbr");
		}
		case ProductInventoryStatus.IS_CUSTOMIZABLE: {
			const days = deliveryEstimateDays.value;
			if (days >= 1 && days <= 14)
				return t("delivery.estimated-weeks-abbr", {amount: "1-2"});
			if (days <= 21)
				return t("delivery.estimated-weeks-abbr", {amount: "2-3"});
			if (days <= 28)
				return t("delivery.estimated-weeks-abbr", {amount: "3-4"});
			if (days <= 35)
				return t("delivery.estimated-weeks-abbr", {amount: "4-5"});
			if (days <= 42)
				return t("delivery.estimated-weeks-abbr", {amount: "5-6"});
			return t("delivery.over-weeks-abbr");
		}
		default: {
			return "";
		}
	}
});
</script>
